import React, { useState, useEffect } from 'react';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { registerUser } from '../api';
import { API_URL } from '../config';

export default function RegisterPage() {
  const [email, setEmail] = useState('');
  const [nickname, setNickname] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [registered, setRegistered] = useState(false);
  const navigate = useNavigate();

  const handleRegister = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!email.trim()) {
      setError('Email is required');
      return;
    }
    
    if (!nickname.trim()) {
      setError('Nickname is required');
      return;
    }
    
    if (nickname.trim().length < 2) {
      setError('Nickname must be at least 2 characters long');
      return;
    }
    
    if (!password) {
      setError('Password is required');
      return;
    }
    
    if (password.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }
    
    if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(password)) {
      setError('Password must contain at least one lowercase letter, one uppercase letter, and one number');
      return;
    }
    
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    setLoading(true);
    setError('');
    try {
      await registerUser(email.trim(), nickname.trim(), password);
      setRegistered(true);
      // Don't redirect immediately - show verification message
    } catch (err) {
      const errorDetail = err.response?.data?.detail || '';
      
      // Handle specific error cases
      if (errorDetail.toLowerCase().includes('email already registered')) {
        setError('This email address is already associated with an account. Please use a different email or try logging in.');
      } else if (errorDetail.toLowerCase().includes('nickname') && 
                (errorDetail.toLowerCase().includes('already') || 
                 errorDetail.toLowerCase().includes('exists') ||
                 errorDetail.toLowerCase().includes('taken'))) {
        setError('This nickname is already taken. Please choose a different nickname.');
      } else if (errorDetail.toLowerCase().includes('unique') || 
                errorDetail.toLowerCase().includes('constraint') ||
                errorDetail.toLowerCase().includes('duplicate')) {
        // Generic database constraint violation - likely nickname
        setError('This nickname is already taken or the email is already registered. Please try different values.');
      } else {
        setError(errorDetail || 'Registration failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleToggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  // ...existing code...

  return (
    <Container maxWidth="xs" sx={{ mt: 8 }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 1,
          mb: 4,
        }}
      >
        <Typography
          variant="h4"
          align="center"
          sx={{
            fontWeight: 700,
            letterSpacing: 1,
            textTransform: 'none',
          }}
        >
          TackleTribe.
        </Typography>
        <Typography variant="subtitle2" align="center" color="text.secondary">
          Deine Crew. Deine Faenge. Dein Tackle.
        </Typography>
        <Typography variant="h6" align="center" sx={{ mt: 1 }}>
          Welcome to TackleTribe!
        </Typography>
      </Box>

      {registered ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h5" color="primary" gutterBottom>
            Registration Successful!
          </Typography>
          <Typography variant="body1" sx={{ mt: 2, mb: 3 }}>
            We've sent a verification email to <strong>{email}</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Please check your inbox and click the verification link to activate your account.
          </Typography>
          <Button
            variant="outlined"
            onClick={() => navigate('/login')}
            sx={{ mt: 2 }}
          >
            Go to Login
          </Button>
        </Box>
      ) : (
        <>
          <Typography variant="h5" align="center" gutterBottom>
            Register
          </Typography>
          <Box component="form" onSubmit={handleRegister}>
            <TextField
              label="Email"
              type="email"
              fullWidth
              margin="normal"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
              autoComplete="email"
              variant="outlined"
            />
            <TextField
              label="Nickname"
              fullWidth
              margin="normal"
              value={nickname}
              onChange={e => setNickname(e.target.value)}
              required
              autoComplete="username"
              variant="outlined"
              helperText="At least 2 characters"
            />
            <TextField
              label="Password"
              type={showPassword ? 'text' : 'password'}
              fullWidth
              margin="normal"
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
              autoComplete="new-password"
              variant="outlined"
              helperText="At least 8 characters with uppercase, lowercase, and number"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleTogglePasswordVisibility}
                      onMouseDown={(e) => e.preventDefault()}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label="Confirm Password"
              type={showConfirmPassword ? 'text' : 'password'}
              fullWidth
              margin="normal"
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              required
              autoComplete="new-password"
              variant="outlined"
              error={confirmPassword && password !== confirmPassword}
              helperText={
                confirmPassword && password !== confirmPassword 
                  ? 'Passwords do not match' 
                  : 'Re-enter your password'
              }
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle confirm password visibility"
                      onClick={handleToggleConfirmPasswordVisibility}
                      onMouseDown={(e) => e.preventDefault()}
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              disabled={loading}
              sx={{ mt: 2 }}
            >
              {loading ? 'Registering...' : 'Register'}
            </Button>
          </Box>
        </>
      )}
      <Snackbar open={!!error} autoHideDuration={8000} onClose={() => setError('')}>
        <Alert severity="error" onClose={() => setError('')} sx={{ maxWidth: 400 }}>
          <Typography variant="body2" component="div">
            {error}
          </Typography>
          {error.toLowerCase().includes('email already') && (
            <Typography variant="caption" component="div" sx={{ mt: 1, opacity: 0.8 }}>
              💡 Try logging in instead, or use a different email address.
            </Typography>
          )}
          {error.toLowerCase().includes('nickname') && error.toLowerCase().includes('taken') && (
            <Typography variant="caption" component="div" sx={{ mt: 1, opacity: 0.8 }}>
              💡 Try adding numbers or variations to make it unique.
            </Typography>
          )}
        </Alert>
      </Snackbar>
    </Container>
  );
}
